export const AVATAR_COLORS = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ec4899",
  "#8b5cf6",
  "#14b8a6",
  "#f97316",
];

export function getAvatarColor(user) {
  const str = user?.id || user?.email || user?.name || user?.Email || user?.Name || "";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function formatProfileDate(dateStr) {
  if (
    !dateStr ||
    dateStr === "0001-01-01T00:00:00" ||
    dateStr === "0001-01-01" ||
    dateStr === "0001-01-01T00:00:00Z"
  ) {
    return null;
  }
  try {
    if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split("-");
      const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
      if (Number.isNaN(date.getTime())) return null;
      return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    }
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return null;
  }
}

export function buildUserProfileInfo(user) {
  if (!user) return null;

  const department = user?.department || user?.Department;
  let departmentName = null;
  if (department) {
    departmentName = typeof department === "string" ? department : department?.name || null;
  }

  const isActive =
    (user?.isActive ??
      user?.isAccountActive ??
      user?.IsActive ??
      user?.IsAccountActive ??
      true) !== false;

  return {
    name: user?.name || user?.Name || "—",
    email: user?.email || user?.Email || "—",
    role:
      user?.roleName ||
      user?.RoleName ||
      (user?.role === 0 || user?.role === 2
        ? "Admin"
        : user?.role === 1 || user?.role === 4
          ? "User"
          : null) ||
      "—",
    designation:
      user?.designationName ??
      user?.DesignationName ??
      user?.designation ??
      user?.Designation ??
      null,
    department: departmentName || (user?.departmentId ? `Department ${user.departmentId}` : "—"),
    office: user?.office || user?.Office || (user?.officeId ? `Office ${user.officeId}` : "—"),
    leaveApprover:
      user?.leaveApprover ||
      user?.LeaveApprover ||
      user?.approvalName ||
      user?.ApprovalName ||
      "—",
    employmentStartDate: formatProfileDate(
      user?.employmentStartDate ||
        user?.EmploymentStartDate ||
        user?.employementStartDate ||
        user?.EmployementStartDate ||
        user?.empStartDate ||
        user?.EmpStartDate
    ),
    accountCreated: formatProfileDate(
      user?.accountCreated || user?.AccountCreated || user?.accountCreationDate || user?.AccountCreationDate
    ),
    isActive,
    imageURL: user?.imageURL || user?.ImageURL || null,
    id: user?.id || user?.Id || user?.userId || user?.UserId || null,
  };
}

const pickText = (source, ...keys) => {
  if (!source) return null;
  for (const key of keys) {
    const value = source[key];
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return null;
};

const toTitleCase = (value) => {
  if (!value) return null;
  const text = String(value).trim();
  if (!text) return null;
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * Normalizes the optional `AdditionalProfile` payload from AppUser/GetUser
 * (gender, date of birth, contacts, identification, address, dependents).
 * Returns null when the payload is missing or has no displayable values.
 */
export function buildAdditionalProfile(user) {
  const source = user?.additionalProfile || user?.AdditionalProfile;
  if (!source || typeof source !== "object") return null;

  const disability = source.disability ?? source.Disability ?? null;

  const rawDependents = source.dependents || source.Dependents;
  const dependents = Array.isArray(rawDependents)
    ? rawDependents
        .map((dep) => ({
          name: pickText(dep, "name", "Name"),
          relationship: toTitleCase(pickText(dep, "relationship", "Relationship")),
          dob: formatProfileDate(dep?.dob || dep?.Dob || dep?.DOB),
          gender: toTitleCase(pickText(dep, "gender", "Gender")),
          contactNumber: pickText(dep, "contactNumber", "ContactNumber"),
        }))
        .filter((dep) => dep.name)
    : [];

  const profile = {
    employeeCode: pickText(source, "employeeCode", "EmployeeCode"),
    gender: toTitleCase(pickText(source, "gender", "Gender")),
    dateOfBirth: formatProfileDate(source.dob || source.Dob || source.DOB),
    maritalStatus: toTitleCase(pickText(source, "maritalStatus", "MaritalStatus")),
    fatherOrHusbandName: pickText(source, "fatherOrHusbandName", "FatherOrHusbandName"),
    bloodGroup: pickText(source, "bloodGroup", "BloodGroup"),
    disability: disability === true ? pickText(source, "disabilityDescription", "DisabilityDescription") || "Yes" : null,
    phoneNumber: pickText(source, "phoneNumber", "PhoneNumber"),
    emergencyContactName: pickText(source, "emergencyContactName", "EmergencyContactName"),
    emergencyContactNumber: pickText(source, "emergencyContactNumber", "EmergencyContactNumber"),
    cnic: pickText(source, "cnic", "CNIC", "Cnic"),
    passportNumber: pickText(source, "passportNumber", "PassportNumber"),
    ntn: pickText(source, "ntn", "NTN", "Ntn"),
    residentialAddress: pickText(source, "residentialAddress", "ResidentialAddress"),
    permanentAddress: pickText(source, "permanentAddress", "PermanentAddress"),
    city: pickText(source, "city", "City"),
    country: pickText(source, "country", "Country"),
    postalCode: pickText(source, "postalCode", "PostalCode"),
    dependents,
  };

  const hasData =
    dependents.length > 0 ||
    Object.entries(profile).some(([key, value]) => key !== "dependents" && value);

  return hasData ? profile : null;
}

export function buildLeaveSummary(user) {
  if (!user) return null;

  const leaveSummaryData = user?.leaveSummary || user?.LeaveSummary;
  if (leaveSummaryData) {
    return {
      activeLeaveTypesCount:
        leaveSummaryData.activeLeaveTypesCount || leaveSummaryData.ActiveLeaveTypesCount || 0,
      totalDaysAllocated:
        leaveSummaryData.totalDaysAllocated || leaveSummaryData.TotalDaysAllocated || 0,
      daysUsed: leaveSummaryData.daysUsed || leaveSummaryData.DaysUsed || 0,
      daysRemaining: leaveSummaryData.daysRemaining || leaveSummaryData.DaysRemaining || 0,
    };
  }

  const settings =
    user?.userLeaveSettings?.filter((lt) => lt.active !== false && lt.Active !== false) ||
    user?.UserLeaveSettings?.filter((lt) => lt.active !== false && lt.Active !== false) ||
    [];

  return {
    activeLeaveTypesCount: settings.length,
    totalDaysAllocated:
      user?.totalLeaveBalance ||
      user?.TotalLeaveBalance ||
      settings.reduce((sum, lt) => sum + (lt.value || lt.Value || lt.totalAllowance || lt.TotalAllowance || 0), 0),
    daysUsed:
      user?.totalUsedLeaves ||
      user?.TotalUsedLeaves ||
      settings.reduce((sum, lt) => sum + (lt.usedLeaves || lt.UsedLeaves || 0), 0),
    daysRemaining:
      user?.totalRemainingLeaves ||
      user?.TotalRemainingLeaves ||
      settings.reduce((sum, lt) => sum + (lt.remainingLeaves || lt.RemainingLeaves || 0), 0),
  };
}

export function getActiveLeaveTypes(user) {
  const settings = user?.userLeaveSettings || user?.UserLeaveSettings || [];
  if (!Array.isArray(settings)) return [];
  return settings.filter((lt) => lt.active !== false && lt.Active !== false);
}
